/** @jsxImportSource @emotion/react */
/* eslint-disable react/prop-types */
import type { ComponentProps } from "react";
import React, {
  Fragment,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { graphql, usePaginationFragment } from "react-relay";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  CellContext,
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useReactTable,
} from "@tanstack/react-table";
import { css } from "@emotion/react";

import { jsonStringToFlatObject } from "@phoenix/utils/jsonUtils";
import { calculateTTFTMs, fmtTTFT } from "@phoenix/utils/ttftUtils";
import {
  Flex,
  Heading,
  Icon,
  Icons,
  Link,
  View,
  Button,
  Text,
  ContextualHelp,
  Tooltip,
  TooltipTrigger,
  TriggerWrap,
} from "@phoenix/components";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { compactResizeHandleCSS } from "@phoenix/components/resize/styles";
import {
  AnnotationSummaryGroupTokens,
  TraceAnnotationSummaryGroupTokens,
} from "@phoenix/components/annotation/AnnotationSummaryGroup";
import { isMirrorAnnotation } from "@phoenix/components/annotation/annotationUtils";
import { MeanScore } from "@phoenix/components/annotation/MeanScore";
import { LoadMoreRow, TextCell } from "@phoenix/components/table";
import { IndeterminateCheckboxCell } from "@phoenix/components/table/IndeterminateCheckboxCell";
import { selectableTableCSS } from "@phoenix/components/table/styles";
import { TableExpandButton } from "@phoenix/components/table/TableExpandButton";
import { TimestampCell } from "@phoenix/components/table/TimestampCell";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanKindToken } from "@phoenix/components/trace/SpanKindToken";
import { SpanStatusCodeIcon } from "@phoenix/components/trace/SpanStatusCodeIcon";
import { TraceTokenCosts } from "@phoenix/components/trace/TraceTokenCosts";
import { TraceTokenCount } from "@phoenix/components/trace/TraceTokenCount";
import type { ISpanItem } from "@phoenix/components/trace/types";
import type { SpanTreeNode } from "@phoenix/components/trace/utils";
import { createSpanTree } from "@phoenix/components/trace/utils";
import { SELECTED_SPAN_NODE_ID_PARAM } from "@phoenix/constants/searchParams";
import { useStreamState } from "@phoenix/contexts/StreamStateContext";
import { useTracingContext } from "@phoenix/contexts/TracingContext";
import { SummaryValueLabels } from "@phoenix/pages/project/AnnotationSummary";
import { MetadataTableCell } from "@phoenix/pages/project/MetadataTableCell";
import { useTracePagination } from "@phoenix/pages/trace/TracePaginationContext";

import type {
  SpanStatusCode,
  TracesTable_spans$key,
} from "./__generated__/TracesTable_spans.graphql";
import type { TracesTableQuery } from "./__generated__/TracesTableQuery.graphql";
import { DEFAULT_PAGE_SIZE } from "./constants";
import { ProjectTableEmpty } from "./ProjectTableEmpty";
import { RetrievalEvaluationLabel } from "./RetrievalEvaluationLabel";
import { SpanColumnSelector } from "./SpanColumnSelector";
import { SpanFilterConditionField } from "./SpanFilterConditionField";
import { SpanSelectionToolbar } from "./SpanSelectionToolbar";
import { FilterSidebar } from "./FilterSidebar";
import { DraggableTableHeader } from "./DraggableTableHeader";
import { spansTableCSS, scrollbarCSS } from "./styles";
import { DEFAULT_SORT, getGqlSort, makeAnnotationColumnId } from "./tableUtils";

type TracesTableProps = {
  project: TracesTable_spans$key;
};

const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const NUM_DESCENDANTS = 50;

interface IAdditionalSpansIndicator {
  /**
   * A flag that if set, indicates that this row is just there to show that there are N more spans under this span
   */
  __additionalRow?: true;
}
/**
 * An indicator that this row is an additional row, not a span
 */
interface IAdditionalSpansRow extends ISpanItem, IAdditionalSpansIndicator { }

/**
 * A nested table row is a span with a children that recursively
 * contains more nested table rows.
 */
type NestedSpanTableRow<TSpan extends IAdditionalSpansRow> = TSpan & {
  children: NestedSpanTableRow<TSpan>[];
};

const TableBody = <T extends { trace: { traceId: string }; id: string }>({
  table,
  hasNext,
  onLoadNext,
  isLoadingNext,
}: {
  table: Table<T>;
  hasNext: boolean;
  onLoadNext: () => void;
  isLoadingNext: boolean;
}) => {
  "use no memo";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { traceId } = useParams();
  const selectedSpanNodeId = searchParams.get(SELECTED_SPAN_NODE_ID_PARAM);

  return (
    <tbody>
      {table.getRowModel().rows.map((row) => {
        const isSelected =
          selectedSpanNodeId === row.original.id ||
          (!selectedSpanNodeId && row.original.trace.traceId === traceId);

        // Machi, trace level-layum PII check pannuvom 🛡️
        const metadata = (row.original as any).metadata;
        let hasPII = false;
        if (metadata) {
          const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
          hasPII = !!(
            metaObj.evalight_pii_detected ||
            metaObj["evalight.pii_detected"] ||
            metaObj.metadata_evalight_pii_detected
          );
        }

        return (
          <tr
            key={row.id}
            data-selected={isSelected}
            data-pii={hasPII}
            css={trCSS}
            style={{ height: "52px", maxHeight: "52px" }}
            onClick={() =>
              navigate(
                `${row.original.trace.traceId}?${SELECTED_SPAN_NODE_ID_PARAM}=${row.original.id}`
              )
            }
          >
            {row.getVisibleCells().map((cell: any) => {
              const colSizeVar = `--col-${cell.column.id}-size`;
              return (
                <td
                  key={cell.id}
                  style={{
                    width: `calc(var(${colSizeVar}) * 1px)`,
                    maxWidth: `calc(var(${colSizeVar}) * 1px)`,
                    height: "52px",
                    maxHeight: "52px",
                    verticalAlign: "middle",
                    padding: "3px 8px",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      height: "46px",
                      maxHeight: "46px",
                      display: "flex",
                      alignItems: "center",
                      overflow: cell.column.id === "annotations" ? "visible" : "hidden",
                      width: "100%",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      })}
      {/* Load more logic... */}
    </tbody>
  );
};

// special memoized wrapper for our table body that we will use during column resizing
export const MemoizedTableBody = React.memo(
  TableBody,
  (prev: any, next: any) => prev.table.options.data === next.table.options.data
) as typeof TableBody;

const MetadataCell = <TData extends ISpanItem, TValue>({
  row,
}: CellContext<TData, TValue>) => {
  if (row.original.__additionalRow) {
    return null;
  }
  return <MetadataTableCell metadata={row.original.metadata} />;
};
const trCSS = css`
  height: 52px !important;
  max-height: 52px !important;
  cursor: pointer;
  border-left: 3px solid transparent; /* default border */

  &:hover {
    background-color: var(--ac-global-color-grey-50);
  }
  &[data-selected="true"] {
    background-color: var(--ac-global-color-blue-50);
  }

  /* Machi, PII Alert style inga dhaan irukku 🎨 */
  &[data-pii="true"] {
    background-color: #181713ff !important; /* light yellow-50 */
    border-left: 3px solid var(--ac-global-color-warning) !important;
  }

  &[data-pii="true"]:hover {
    background-color: #1b1a14ff !important; /* light yellow-100 */
  }

  &[data-pii="true"][data-selected="true"] {
    background-color: #42413aff !important;
    border-left: 3px solid var(--ac-global-color-warning) !important;
  }

  .theme--dark &[data-pii="true"] {
    background-color: #2a2a2a !important; /* lite grey color for dark theme */
    border-left: 3px solid var(--ac-global-color-warning) !important;
  }

  .theme--dark &[data-pii="true"]:hover {
    background-color: #3a3a3a !important;
  }

  .theme--dark &[data-pii="true"][data-selected="true"] {
    background-color: #3e3e3e !important;
    border-left: 3px solid var(--ac-global-color-warning) !important;
  }

  td {
    padding: 0 8px !important;
    vertical-align: middle;
    height: 24px !important;
    border-bottom: 0.5px solid var(--ac-global-color-grey-200);
    font-size: 12px;
  }
`;

/**
 * Recursively create a nested table rows to display the span tree
 * as a table.
 */
function spanTreeToNestedSpanTableRows<TSpan extends ISpanItem>(params: {
  children: SpanTreeNode<TSpan>[];
}): NestedSpanTableRow<TSpan>[] {
  const { children } = params;
  const normalizedSpanTreeChildren: NestedSpanTableRow<TSpan>[] = [];
  for (const child of children) {
    const normalizedChild = {
      ...child.span,
      children: spanTreeToNestedSpanTableRows({
        children: child.children,
      }),
    };
    normalizedSpanTreeChildren.push(normalizedChild);
  }
  return normalizedSpanTreeChildren;
}

export function TracesTable(props: TracesTableProps) {
  //we need a reference to the scrolling element for logic down below
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterCondition, setFilterCondition] = useState<string>("");
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(() => {
    const saved = localStorage.getItem("filterSidebarVisible");
    return saved !== null ? saved === "true" : true;
  });
  const toggleSidebar = (val: boolean) => {
    setSidebarVisible(val);
    localStorage.setItem("filterSidebarVisible", String(val));
  };
  const { fetchKey } = useStreamState();

  const { data, loadNext, hasNext, isLoadingNext, refetch } =
    usePaginationFragment<TracesTableQuery, TracesTable_spans$key>(
      graphql`
        fragment TracesTable_spans on Project
        @refetchable(queryName: "TracesTableQuery")
        @argumentDefinitions(
          after: { type: "String", defaultValue: null }
          first: { type: "Int", defaultValue: 30 }
          sort: { type: "SpanSort", defaultValue: { col: startTime, dir: desc } }
          filterCondition: { type: "String", defaultValue: null }
          numDescendants: { type: "Int", defaultValue: 50 }
        ) {
          name
          ...SpanColumnSelector_annotations
          rootSpans: spans(
            first: $first
            after: $after
            sort: $sort
            rootSpansOnly: true
            filterCondition: $filterCondition
            timeRange: $timeRange
          ) @connection(key: "TracesTable_rootSpans") {
            edges {
              rootSpan: node {
                id
                spanKind
                name
                metadata
                attributes
                agentName
                username
                keyAlias
                modelName
                sessionId
                statusCode
                startTime
                endTime
                latencyMs
                cumulativeTokenCountTotal
                parentId
                input {
                  value: truncatedValue
                }
                output {
                  value: truncatedValue
                }
                spanId
                trace {
                  id
                  traceId
                  numSpans
                  costSummary {
                    total {
                      cost
                    }
                  }
                  traceAnnotations {
                    id
                    name
                    label
                    score
                    annotatorKind
                    metadata
                    user {
                      username
                      profilePictureUrl
                    }
                  }
                  traceAnnotationSummaries {
                    labelFractions {
                      fraction
                      label
                    }
                    meanScore
                    name
                  }
                }
                spanAnnotations {
                  id
                  name
                  label
                  score
                  annotatorKind
                  createdAt
                  metadata
                }
                spanAnnotationSummaries {
                  labelFractions {
                    fraction
                    label
                  }
                  meanScore
                  name
                }
                ...AnnotationSummaryGroup
                documentRetrievalMetrics {
                  evaluationName
                  ndcg
                  precision
                  hit
                }
                descendants(first: $numDescendants) {
                  edges {
                    node {
                      id
                      spanKind
                      name
                      agentName
                      username
                      keyAlias
                      modelName
                      attributes
                      sessionId
                      statusCode: propagatedStatusCode
                      startTime
                      endTime
                      latencyMs
                      parentId
                      cumulativeTokenCountTotal: cumulativeTokenCountTotal
                      input {
                        value: truncatedValue
                      }
                      output {
                        value: truncatedValue
                      }
                      spanId
                      trace {
                        id
                        traceId
                      }
                      spanAnnotations {
                        id
                        name
                        label
                        score
                        annotatorKind
                        createdAt
                        metadata
                      }
                      ...AnnotationSummaryGroup
                      documentRetrievalMetrics {
                        evaluationName
                        ndcg
                        precision
                        hit
                      }
                      ...TraceHeaderRootSpanAnnotationsFragment
                    }
                  }
                }
              }
            }
          }
        }
      `,
      props.project
    );

  const annotationColumnVisibility = useTracingContext(
    (state) => state.annotationColumnVisibility
  );
  const visibleAnnotationColumnNames = useMemo(() => {
    return Object.keys(annotationColumnVisibility).filter(
      (name) => annotationColumnVisibility[name]
    );
  }, [annotationColumnVisibility]);
  const tableData = useMemo(() => {
    return data.rootSpans.edges.map(({ rootSpan }) => {
      // Construct the set of spans over which you want to construct the tree
      const spanTree = createSpanTree([
        rootSpan,
        ...rootSpan.descendants.edges.map(({ node }) => node),
      ]);
      // Unwrap the root span from the span tree and return it
      const [root] = spanTreeToNestedSpanTableRows({
        children: spanTree,
      });
      type SpanRowType = typeof root & IAdditionalSpansRow;
      // check if there are more spans in the tree than is loaded
      const numSpansNotLoaded = rootSpan.trace.numSpans - NUM_DESCENDANTS - 1;
      if (numSpansNotLoaded > 0) {
        root.children = [
          ...root.children,
          // We add a dummy span here to indicate that there are more spans in the tree
          {
            ...root,
            // Indicate that this is an additional row, not a span
            __additionalRow: true,
            name: `+ ${numSpansNotLoaded} more span${numSpansNotLoaded > 1 ? "s" : ""}`,
            id: `additional-${root.id}`,
            // Clear out the span info
            input: { value: "" },
            output: { value: "" },
            metadata: null,
            spanAnnotations: [],
            documentRetrievalMetrics: [],
            children: [],
          } as SpanRowType,
        ];
      }
      return root as SpanRowType;
    });
  }, [data]);
  type TableRow = (typeof tableData)[number];

  type AnnotationSummaryLike = {
    name: string;
    meanScore: number | null;
    labelFractions: readonly { label: string; fraction: number }[];
  };

  /**
   * Prefer the canonical run-level (trace) annotation summary for a root span,
   * falling back to the span-level summary for step/observation rows.
   */
  const getAnnotationSummary = (
    row: TableRow,
    name: string
  ): AnnotationSummaryLike | undefined => {
    const trace = row.trace as
      | { traceAnnotationSummaries?: readonly AnnotationSummaryLike[] }
      | undefined;
    const traceSummary = trace?.traceAnnotationSummaries?.find(
      (summary) => summary.name === name
    );
    if (traceSummary) {
      return traceSummary;
    }
    const spanSummaries = row.spanAnnotationSummaries as
      | readonly AnnotationSummaryLike[]
      | undefined;
    return spanSummaries?.find((summary) => summary.name === name);
  };

  const dynamicAnnotationColumns: ColumnDef<TableRow>[] = useMemo(
    () =>
      visibleAnnotationColumnNames.map((name) => {
        return {
          header: name,
          columns: [
            {
              header: `labels`,
              accessorKey: makeAnnotationColumnId(name, "label"),
              cell: ({ row }: CellContext<TableRow, any>) => {
                const annotation = getAnnotationSummary(row.original, name);
                if (!annotation) {
                  return null;
                }
                return (
                  <SummaryValueLabels
                    name={name}
                    labelFractions={annotation.labelFractions}
                  />
                );
              },
            } as ColumnDef<TableRow>,
            {
              header: `mean score`,
              accessorKey: makeAnnotationColumnId(name, "score"),
              cell: ({ row }: CellContext<TableRow, any>) => {
                const annotation = getAnnotationSummary(row.original, name);
                if (!annotation) {
                  return null;
                }
                return (
                  <MeanScore value={annotation.meanScore} fallback={null} />
                );
              },
            } as ColumnDef<TableRow>,
          ],
        };
      }),
    [visibleAnnotationColumnNames]
  );

  const annotationColumns: ColumnDef<TableRow>[] = useMemo(
    () => [
      {
        header: () => (
          <Flex direction="row" gap="size-50" alignItems="center">
            <span>Annotations</span>
            <ContextualHelp>
              <Heading level={3} weight="heavy">
                Annotations
              </Heading>
              <Text>
                Evaluations and human annotations logged via the API or set via
                the UI.
              </Text>
            </ContextualHelp>
          </Flex>
        ),
        id: "annotations",
        accessorKey: "spanAnnotations",
        enableSorting: false,
        cell: ({ row }: CellContext<TableRow, any>) => {
          if (row.original.__additionalRow) {
            return null;
          }
          // The root span's row represents a whole run: show the canonical
          // run-level (trace) verdicts first and keep observation-level
          // annotations as the diagnostic layer beneath them. Mirror
          // annotations are excluded so the same verdict is not shown twice.
          const isRootSpan = row.depth === 0;
          const trace = row.original.trace;
          const traceAnnotations =
            isRootSpan && "traceAnnotations" in trace
              ? trace.traceAnnotations
              : [];
          const traceSummaries =
            isRootSpan && "traceAnnotationSummaries" in trace
              ? trace.traceAnnotationSummaries
              : [];
          const visibleSpanAnnotations = row.original.spanAnnotations.filter(
            (annotation) => !isMirrorAnnotation(annotation)
          );
          const hasNoFeedback =
            traceSummaries.length === 0 &&
            visibleSpanAnnotations.length === 0 &&
            row.original.documentRetrievalMetrics.length === 0;
          return (
            <div
              style={{
                maxHeight: "46px",
                height: "46px",
                overflowY: "auto",
                overflowX: "hidden",
                width: "100%",
                scrollbarWidth: "thin",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Flex direction="row" gap="size-50" wrap="wrap">
                {traceSummaries.length > 0 ? (
                  <TraceAnnotationSummaryGroupTokens
                    annotations={traceAnnotations}
                    summaries={traceSummaries}
                    showFilterActions
                  />
                ) : null}
                <AnnotationSummaryGroupTokens
                  span={row.original}
                  showFilterActions
                  excludeMirrorAnnotations={isRootSpan}
                />
                {row.original.documentRetrievalMetrics.map((retrievalMetric) => {
                  return (
                    <Fragment key={retrievalMetric.evaluationName + retrievalMetric.precision}>
                      <RetrievalEvaluationLabel
                        key="ndcg"
                        name={retrievalMetric.evaluationName}
                        metric="ndcg"
                        score={retrievalMetric.ndcg}
                      />
                      <RetrievalEvaluationLabel
                        key="precision"
                        name={retrievalMetric.evaluationName}
                        metric="precision"
                        score={retrievalMetric.precision}
                      />
                      <RetrievalEvaluationLabel
                        key="hit"
                        name={retrievalMetric.evaluationName}
                        metric="hit"
                        score={retrievalMetric.hit}
                      />
                    </Fragment>
                  );
                })}
                {hasNoFeedback ? "--" : null}
              </Flex>
            </div>
          );
        },
      },
      ...dynamicAnnotationColumns,
    ],
    [dynamicAnnotationColumns]
  );

  const columns: ColumnDef<TableRow>[] = useMemo(
    () => [
      {
        id: "select",
        maxSize: 32,
        header: ({ table }) => (
          <IndeterminateCheckboxCell
            {...{
              isSelected: table.getIsAllRowsSelected(),
              isIndeterminate: table.getIsSomeRowsSelected(),
              onChange: table.toggleAllRowsSelected,
            }}
          />
        ),
        cell: ({ row }: CellContext<TableRow, any>) => {
          if (row.original.__additionalRow) {
            return null;
          }
          return (
            <IndeterminateCheckboxCell
              {...{
                isSelected: row.getIsSelected(),
                isDisabled: !row.getCanSelect(),
                isIndeterminate: row.getIsSomeSelected(),
                onChange: row.toggleSelected,
              }}
            />
          );
        },
      },
      {
        header: "status",
        accessorKey: "statusCode",
        enableSorting: false,
        minSize: 75,
        maxSize: 95,
        cell: ({ getValue, row }: CellContext<TableRow, any>) => {
          if (row.original.__additionalRow) {
            return null;
          }
          const statusCode = getValue() as SpanStatusCode;
          const metadata = row.original.metadata;
          let hasPII = false;
          let piiTypes = "";

          if (metadata) {
            const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
            hasPII = !!(
              metaObj.evalight_pii_detected ||
              metaObj["evalight.pii_detected"] ||
              metaObj.metadata_evalight_pii_detected
            );
            piiTypes = String(
              metaObj.evalight_pii_types ||
              metaObj["evalight.pii_types"] ||
              metaObj.metadata_evalight_pii_types ||
              ""
            );
            // Machi, confidence level fetch pannuvom 🏷️
            const piiConfidence = String(
              metaObj.evalight_pii_confidence ||
              metaObj["evalight.pii_confidence"] ||
              metaObj.metadata_evalight_pii_confidence ||
              "MEDIUM"
            );
            (row.original as any)._piiConfidence = piiConfidence; // Save for tooltip
          }

          return (
            <Flex direction="row" gap="size-50" alignItems="center">
              <SpanStatusCodeIcon statusCode={statusCode} />
              {hasPII && (
                <TooltipTrigger delay={200}>
                  <TriggerWrap>
                    <Icon
                      svg={<Icons.ShieldOutline />}
                      color="warning"
                    />
                  </TriggerWrap>
                  <Tooltip>
                    PII Detected (Confidence: {(row.original as any)._piiConfidence || "MEDIUM"}): {piiTypes}
                  </Tooltip>
                </TooltipTrigger>
              )}
            </Flex>
          );
        },
      },
      {
        header: ({ table }: { table: Table<TableRow> }) => {
          return (
            <Flex gap="size-50" direction="row" alignItems="center">
              <TableExpandButton
                isExpanded={table.getIsAllRowsExpanded()}
                onClick={table.getToggleAllRowsExpandedHandler()}
                aria-label="Expand all rows"
              />
              kind
            </Flex>
          );
        },
        enableSorting: false,
        accessorKey: "spanKind",
        maxSize: 100,
        cell: (props) => {
          if (props.row.original.__additionalRow) {
            return (
              <div
                css={css`
                  // Since rows are flattened by default,
                  // we can use the row.depth property
                  // and paddingLeft to visually indicate the depth
                  // of the row
                  padding-left: ${props.row.depth * 1}rem;
                `}
              >
                <Icon svg={<Icons.MoreHorizontalOutline />} />
              </div>
            );
          }

          return (
            <div
              css={css`
                // Since rows are flattened by default,
                // we can use the row.depth property
                // and paddingLeft to visually indicate the depth
                // of the row
                padding-left: ${props.row.depth * 1}rem;
              `}
            >
              <Flex gap="size-50">
                {props.row.getCanExpand() ? (
                  <TableExpandButton
                    isExpanded={props.row.getIsExpanded()}
                    onClick={props.row.getToggleExpandedHandler()}
                    aria-label="Expand row"
                  />
                ) : null}
                <SpanKindToken spanKind={props.getValue() as string} />
              </Flex>
            </div>
          );
        },
      },
      {
        header: "name",
        accessorKey: "name",
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const { traceId } = row.original.trace;
          const spanId = row.original.__additionalRow ? null : row.original.id;
          const nameVal = (getValue() as string) || "--";
          return (
            <Link
              to={`${traceId}${spanId ? `?${SELECTED_SPAN_NODE_ID_PARAM}=${spanId}` : ""}`}
              title={nameVal}
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 600;
                font-size: 13px;
                color: #00685f;
                text-decoration: none;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                max-width: 100%;
                &:hover {
                  text-decoration: underline;
                  color: #004d47;
                }
                .theme--dark & {
                  color: #14bab6;
                  &:hover {
                    color: #2dd4bf;
                  }
                }
              `}
            >
              {nameVal}
            </Link>
          );
        },
      },
      {
        header: "agent name",
        accessorKey: "agentName",
        enableSorting: false,
        cell: ({ getValue }) => {
          const val = getValue();
          if (!val || val === "--") {
            return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
          }
          return (
            <span
              title={String(val)}
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 500;
                font-size: 12.5px;
                color: #0f172a;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                max-width: 100%;
                .theme--dark & {
                  color: #f8fafc;
                }
              `}
            >
              {String(val)}
            </span>
          );
        },
      },
      {
        header: "username",
        accessorKey: "username",
        enableSorting: true,
        cell: ({ getValue }) => {
          const val = getValue();
          if (!val || val === "--") {
            return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
          }
          return (
            <span
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 400;
                font-size: 13px;
                color: #334155;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                max-width: 100%;
                .theme--dark & {
                  color: #cbd5e1;
                }
              `}
            >
              {String(val)}
            </span>
          );
        },
      },
      {
        header: "key alias",
        accessorKey: "keyAlias",
        enableSorting: true,
        cell: ({ getValue, row }: CellContext<TableRow, any>) => {
          const val = getValue();
          let alias = val ? String(val) : null;
          if (!alias) {
            const metadata = row.original.metadata;
            if (metadata) {
              const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
              alias =
                metaObj.user_api_key_alias ||
                metaObj["metadata.user_api_key_alias"] ||
                metaObj.user_api_key_hash ||
                metaObj["metadata.user_api_key_hash"] ||
                null;
            }
          }
          if (!alias || alias === "--") {
            return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
          }
          return (
            <span
              title={alias}
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 400;
                font-size: 12px;
                color: #64748b;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                max-width: 100%;
                .theme--dark & {
                  color: #94a3b8;
                }
              `}
            >
              {alias}
            </span>
          );
        },
      },
      {
        header: "model",
        accessorKey: "modelName",
        enableSorting: true,
        cell: ({ getValue, row }: CellContext<TableRow, any>) => {
          const val = getValue();
          let model = val ? String(val) : null;

          if (!model) {
            const extractFromAttrs = (attrs: any): string | null => {
              if (!attrs) return null;
              const obj = typeof attrs === "string" ? jsonStringToFlatObject(attrs) : attrs;
              return (
                obj["llm.model_name"] ||
                obj["llm.model"] ||
                obj.model_name ||
                obj.model ||
                obj["llm.response.model"] ||
                obj["gen_ai.request.model"] ||
                obj["gen_ai.response.model"] ||
                obj.llm_model_name ||
                null
              );
            };

            // 1. Check root span attributes
            const rootAttrModel = extractFromAttrs(row.original.attributes);
            if (rootAttrModel) model = rootAttrModel;
            else {
              // 2. Check metadata
              const metaModel = extractFromAttrs(row.original.metadata);
              if (metaModel) model = metaModel;
              else if (row.original.descendants?.edges) {
                // 3. Check child descendant spans
                for (const edge of row.original.descendants.edges) {
                  const child = edge.node;
                  if (child.modelName) {
                    model = String(child.modelName);
                    break;
                  }
                  const childAttrModel = extractFromAttrs(child.attributes);
                  if (childAttrModel) {
                    model = childAttrModel;
                    break;
                  }
                }
              }
            }
          }

          if (!model || model === "--") {
            return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
          }

          return (
            <span
              title={model}
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 500;
                font-size: 12px;
                color: #0f172a;
                background-color: rgba(15, 23, 42, 0.05);
                padding: 2px 6px;
                border-radius: 4px;
                border: 1px solid rgba(15, 23, 42, 0.08);
                display: inline-block;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                .theme--dark & {
                  color: #f1f5f9;
                  background-color: rgba(241, 245, 249, 0.08);
                  border-color: rgba(241, 245, 249, 0.12);
                }
              `}
            >
              {model}
            </span>
          );
        },
      },
      {
        header: "session id",
        accessorKey: "sessionId",
        enableSorting: true,
        cell: ({ getValue }) => {
          const val = getValue();
          if (!val || val === "--") {
            return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
          }
          return (
            <span
              title={String(val)}
              css={css`
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
                font-weight: 500;
                font-size: 12px;
                color: #64748b;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                max-width: 100%;
                .theme--dark & {
                  color: #94a3b8;
                }
              `}
            >
              {String(val)}
            </span>
          );
        },
      },
      {
        header: "input",
        accessorKey: "input.value",
        enableSorting: false,
        cell: TextCell,
      },
      {
        header: "output",
        accessorKey: "output.value",
        enableSorting: false,
        cell: TextCell,
      },
      {
        header: "metadata",
        accessorKey: "metadata",
        enableSorting: false,
        cell: MetadataCell,
      },
      ...annotationColumns, // TODO: consider hiding this column is there is no evals. For now show it
      {
        header: "start time",
        accessorKey: "startTime",
        cell: (props) => {
          if (props.row.original.__additionalRow) {
            return null;
          }
          return <TimestampCell {...props} />;
        },
      },
      {
        header: "latency",
        accessorKey: "latencyMs",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (
            value === null ||
            typeof value !== "number" ||
            row.original.__additionalRow
          ) {
            return null;
          }
          return <LatencyText latencyMs={value} />;
        },
      },
      {
        header: "TTFT",
        id: "ttft",
        cell: ({ row }: CellContext<TableRow, any>) => {
          if (row.original.__additionalRow) return null;
          const ttftMs = calculateTTFTMs(row.original);
          return fmtTTFT(ttftMs);
        },
      },
      {
        header: "total tokens",
        minSize: 80,
        accessorKey: "cumulativeTokenCountTotal",
        cell: ({ row, getValue }) => {
          if (row.original.__additionalRow) {
            return null;
          }
          const value = getValue();
          if (value === null) {
            return "--";
          }
          return (
            <TraceTokenCount
              tokenCountTotal={value as number}
              nodeId={row.original.trace.id}
            />
          );
        },
      },
      {
        header: "total cost",
        minSize: 80,
        accessorKey: "trace.costSummary.total.cost",
        id: "cumulativeTokenCostTotal",
        cell: ({ row, getValue }) => {
          const value = getValue();
          if (value === null || typeof value !== "number") {
            return "--";
          }
          const span = row.original;
          return (
            <TraceTokenCosts
              totalCost={value}
              nodeId={span.trace.id}
              size="S"
            />
          );
        },
      },
    ],
    [annotationColumns]
  );

  useEffect(() => {
    if (isFirstRender.current === true) {
      // Skip the first render. The data is already fetched by the parent
      isFirstRender.current = false;
      return;
    }
    //if the sorting changes, we need to reset the pagination
    const sort = sorting[0];
    startTransition(() => {
      refetch(
        {
          sort: sort ? getGqlSort(sort) : DEFAULT_SORT,
          after: null,
          first: PAGE_SIZE,
          filterCondition: filterCondition,
          numDescendants: NUM_DESCENDANTS,
        },
        {
          fetchPolicy: "store-and-network",
        }
      );
    });
  }, [sorting, refetch, filterCondition, fetchKey]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isLoadingNext &&
          hasNext
        ) {
          loadNext(PAGE_SIZE);
        }
      }
    },
    [hasNext, isLoadingNext, loadNext]
  );

  const pagination = useTracePagination();
  const setTraceSequence = pagination?.setTraceSequence;
  useEffect(() => {
    if (!setTraceSequence) {
      return;
    }
    setTraceSequence(
      data.rootSpans.edges.map(({ rootSpan }) => ({
        traceId: rootSpan.trace.traceId,
        spanId: rootSpan.id,
      }))
    );
    return () => {
      setTraceSequence([]);
    };
  }, [data.rootSpans.edges, setTraceSequence]);

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const columnVisibility = useTracingContext((state) => state.columnVisibility);
  const setColumnSizing = useTracingContext((state) => state.setColumnSizing);
  const columnSizing = useTracingContext((state) => state.columnSizing);

  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  const table = useReactTable<TableRow>({
    data: tableData,
    columns,
    onExpandedChange: setExpanded,
    manualSorting: true,
    getSubRows: (row) => row.children,
    state: {
      sorting,
      expanded,
      columnVisibility,
      rowSelection,
      columnSizing,
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    columnResizeMode: "onChange",
    onRowSelectionChange: setRowSelection,
    enableSubRowSelection: false,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  // Set initial column order if not set
  useEffect(() => {
    if (columnOrder.length === 0 && table.getAllLeafColumns().length > 0) {
      setColumnOrder(table.getAllLeafColumns().map((d: any) => d.id));
    }
  }, [table, columnOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder((items: string[]) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const rows = table.getRowModel().rows;
  const selectedRows = table.getSelectedRowModel().flatRows;
  const selectedSpans = selectedRows.map((row) => ({
    id: row.original.id,
    traceId: row.original.trace.id,
  }));
  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);
  const isEmpty = rows.length === 0;
  const computedColumns = table.getAllColumns().filter((column) => {
    // Filter out columns that are eval groupings
    return column.columns.length === 0;
  });
  const { columnSizingInfo, columnSizing: columnSizingState } =
    table.getState();
  const getFlatHeaders = table.getFlatHeaders;
  const colLength = computedColumns.length;
  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   * @see https://tanstack.com/table/v8/docs/framework/react/examples/column-resizing-performant
   */
  const columnSizeVars = React.useMemo(() => {
    const headers = getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
    // Disabled lint as per tanstack docs linked above

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFlatHeaders, columnSizingInfo, columnSizingState, colLength]);

  return (
    <div css={spansTableCSS}>
      <View
        paddingTop="size-100"
        paddingBottom="size-100"
        paddingStart="size-200"
        paddingEnd="size-200"
        borderBottomColor="default"
        borderBottomWidth="thin"
        flex="none"
      >
        <Flex direction="row" gap="size-100" width="100%" alignItems="center">
          <Button
            css={css`
              min-width: 100px;
            `}
            onPress={() => toggleSidebar(!sidebarVisible)}
          >
            {sidebarVisible ? "Hide Filter" : "Show Filter"}
          </Button>
          <SpanFilterConditionField onValidCondition={setFilterCondition} />
          <SpanColumnSelector columns={computedColumns} query={data} />
        </Flex>
      </View>
      <PanelGroup direction="horizontal">
        {sidebarVisible && (
          <>
            <Panel defaultSize={20} minSize={15}>
              <FilterSidebar
                items={tableData as any[]}
              />
            </Panel>
            <PanelResizeHandle css={compactResizeHandleCSS} />
          </>
        )}
        <Panel>
          <div
            css={css`
              height: 100%;
              overflow: auto;
              ${scrollbarCSS};
            `}
            onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
            ref={tableContainerRef}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table
                css={selectableTableCSS}
                style={{
                  ...columnSizeVars,
                  width: table.getTotalSize(),
                  minWidth: "100%",
                }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <SortableContext
                      key={headerGroup.id}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <tr key={headerGroup.id} style={{ height: '28px' }}>
                        {headerGroup.headers.map((header) => (
                          <DraggableTableHeader key={header.id} header={header} />
                        ))}
                      </tr>
                    </SortableContext>
                  ))}
                </thead>
                <tbody>
                  {isEmpty && !hasNext ? (
                    <tr>
                      <td colSpan={table.getVisibleLeafColumns().length}>
                        <ProjectTableEmpty projectName={data.name} />
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((row) => (
                        <tr
                          key={row.id}
                          css={trCSS}
                          onClick={() =>
                            navigate(
                              `${row.original.trace.traceId}?${SELECTED_SPAN_NODE_ID_PARAM}=${row.original.id}`
                            )
                          }
                        >
                          {row.getVisibleCells().map((cell) => {
                            const colSizeVar = `--col-${cell.column.id}-size`;
                            return (
                              <td
                                key={cell.id}
                                style={{
                                  width: `calc(var(${colSizeVar}) * 1px)`,
                                  maxWidth: `calc(var(${colSizeVar}) * 1px)`,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <div style={{ height: "24px", display: "flex", alignItems: "center" }}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {hasNext && !isLoadingNext && (
                        <LoadMoreRow
                          colSpan={table.getVisibleLeafColumns().length}
                          onLoadMore={() => loadNext(PAGE_SIZE)}
                        />
                      )}
                      {isLoadingNext && (
                        <LoadMoreRow
                          colSpan={table.getVisibleLeafColumns().length}
                          isLoading
                        />
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </DndContext>
          </div>
        </Panel>
      </PanelGroup>
      {selectedRows.length ? (
        <SpanSelectionToolbar
          selectedSpans={selectedSpans}
          onClearSelection={clearSelection}
        />
      ) : null}
    </div>
  );
}
